import { Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import DataLoader from "../Dataloader";
import BaseLayout from "../BaseLayout";
import PrivateRoute from "../routes/PrivateRoutes";
import Dashboard from "../pages/Dashboard";
import Users from "../pages/users/Users";
import Roles from "../pages/users/Roles";
import RolePermissions from "../pages/users/RolePermissions";
import CampaignsList from "../pages/campaigns/CampaignsList";
import CampaignsSearch from "../pages/campaigns/CampaignsSearch";
import CampaignsWorkspace from "../pages/campaigns/CampaignsWorkspace";
import NotFound from "../pages/NotFound";
import NoAccess from "../pages/NoAccess";
import SessionExpired from "../pages/SessionExpired";
import Groups from "../pages/users/Groups";
import Profile from "../pages/auth/Profile";
import SSOConfig from "../pages/auth/SSOConfig";
import Tenants from "../pages/tenants/Tenants";
import TenantFormPage from "../pages/tenants/TenantFormPage";
import TenantInfrastructure from "../pages/tenants/TenantInfrastructure";
import WebHook from "../pages/webhooks/WebHook";
import BusinessSettings from "../pages/settings/BusinessSettings";
import MCPEndpoints from "../pages/mcp/MCPEndpoints";
import Documents from "../pages/documents/Documents";
import FileManagerUI from "../pages/documents/Files";
import ParseFile from "../pages/documents/ParseFile";
import InviteUsers from "../pages/users/InviteUsers";
import AuthBaseLayout from "../layouts/AuthBaseLayout";
import DatabaseConfig from "../pages/databaseConfig/DatabaseConfig";
import RegistrationSuccessMessage from "../components/auth/RegisterSuccessMessage";
import AuditTrailReports from "../pages/auditTrail/AuditTrailReports";
import ApiEndpoint from "../pages/apiEndponits/ApiEndpoint";
import Templates from "../pages/template/templates";
import TemplateFormPage from "../components/template/TemplateFormPage";
import TemplatePreview from "../components/template/TemplatePreview";
import DocumentSearch from "../pages/documents/DocumentSearch";
import DocumentCompare from "../pages/documents/DocumentCompare";
import DocumentCompareResult from "../pages/documents/DocumentCompareResult";
import WorkflowLayout from "../layouts/WorkflowLayout";
import WorkflowListPage from "../workflow/WorkflowListPage";
import CreateWorkflowPage from "../workflow/CreateWorkflowPage";

import DocumentProcessingReport from "../pages/reports/DocumentProcessingReport";
import UserActivityReports from "../pages/reports/UserActivityReports";
import TokenCookieHandler from "../pages/auth/TokenCookieHandler";
import NotificationManagement from "../pages/notifications/NotificationManagement";
import WorkFlowExecutions from "../workflow/workflowExecution";
import OneDriveAuthHandler from "../workflow/services/oneDriveAuthHandler";
import GmailAuthHandler from "../workflow/services/gmailAuthHandler";
import OutlookAuthHandler from "../workflow/services/outlookAuthHandler";
import WorkflowNodeDocs from "../workflow/docs/WorkflowNodeDocs";
import WebScraperJobList from "../pages/documents/WebScraperJobList";
import WebScraperJobDetail from "../pages/documents/WebScraperJobDetail";
import SpreadsheetConnectorList from "../pages/documents/SpreadsheetConnectorList";
import SpreadsheetConnectorCallback from "../pages/documents/SpreadsheetConnectorCallback";
import FolderConnectionsPage from "../pages/documents/FolderConnectionsPage";
import RequirePermission from "./RequirePermission";

interface RouteEntry {
  path: string;
  element: JSX.Element;
  isProtected: boolean;
  /** Single permission required to view this route. */
  permission?: string;
  /** OR — user must have at least one of these to view this route. */
  anyOf?: string[];
}

const routeConfig: RouteEntry[] = [
  { path: "/", element: <DataLoader />, isProtected: true },
  {
    path: "/dashboard",
    element: <Dashboard />,
    isProtected: true,
    permission: "dashboard:view",
  },

  // User management
  {
    path: "/users",
    element: <Users />,
    isProtected: false,
    permission: "users:view",
  },
  {
    path: "/roles",
    element: <Roles />,
    isProtected: false,
    permission: "roles:view",
  },
  {
    path: "/roles/:role_key/permissions",
    element: <RolePermissions />,
    isProtected: false,
    permission: "roles:view",
  },
  {
    path: "/groups",
    element: <Groups />,
    isProtected: false,
    permission: "groups:view",
  },
  {
    path: "/admin/users/invitations",
    element: <InviteUsers />,
    isProtected: false,
    permission: "users:invite",
  },

  // AI Decision Intelligence (Campaigns)
  {
    path: "/campaigns",
    element: <CampaignsList />,
    isProtected: false,
    anyOf: ["documents:view", "documents:search"],
  },
  {
    path: "/campaigns/search",
    element: <CampaignsSearch />,
    isProtected: false,
    anyOf: ["documents:view", "documents:search"],
  },
  {
    path: "/campaigns/:id/workspace",
    element: <CampaignsWorkspace />,
    isProtected: false,
    anyOf: ["documents:view", "documents:search"],
  },

  // Profile is always available to a signed-in user — no permission gate.
  { path: "/profile", element: <Profile />, isProtected: false },

  // Integrations
  {
    path: "/integrations/webhooks",
    element: <WebHook />,
    isProtected: false,
    permission: "integrations:view",
  },
  {
    path: "/integrations/mcp-endpoints",
    element: <MCPEndpoints />,
    isProtected: false,
    permission: "integrations:view",
  },
  {
    path: "/integrations/database-config",
    element: <DatabaseConfig />,
    isProtected: false,
    permission: "integrations:view",
  },
  {
    path: "/integrations/api-endpoints",
    element: <ApiEndpoint />,
    isProtected: true,
    permission: "integrations:view",
  },

  // Business + Documents
  {
    path: "/business-settings",
    element: <BusinessSettings />,
    isProtected: false,
    permission: "settings:view",
  },
  {
    path: "/documents",
    element: <Documents />,
    isProtected: false,
    permission: "documents:view",
  },
  {
    path: "/files/:documentStoreId",
    element: <FileManagerUI />,
    isProtected: false,
    permission: "documents:view",
  },
  {
    path: "/file-parser",
    element: <ParseFile />,
    isProtected: false,
    permission: "documents:parse",
  },

  // Enterprise
  {
    path: "/enterprise/sso-config",
    element: <SSOConfig />,
    isProtected: false,
    permission: "enterprise:sso_config",
  },
  {
    path: "/enterprise/tenants",
    element: <Tenants />,
    isProtected: false,
    permission: "enterprise:tenants",
  },
  {
    path: "/enterprise/tenants/create",
    element: <TenantFormPage />,
    isProtected: false,
    permission: "enterprise:tenants",
  },
  {
    path: "/enterprise/tenants/edit/:id",
    element: <TenantFormPage />,
    isProtected: false,
    permission: "enterprise:tenants",
  },
  {
    path: "/enterprise/tenants/:tenant_key/infrastructure",
    element: <TenantInfrastructure />,
    isProtected: false,
    permission: "enterprise:tenants",
  },

  // Reports
  {
    path: "/audit-trail",
    element: <AuditTrailReports />,
    isProtected: false,
    permission: "reports:audit_trail",
  },
  {
    path: "/user-activity-report",
    element: <UserActivityReports />,
    isProtected: true,
    permission: "reports:user_activity",
  },
  {
    path: "/document-processing-report",
    element: <DocumentProcessingReport />,
    isProtected: true,
    permission: "reports:document_processing",
  },

  // Templates
  {
    path: "/templates",
    element: <Templates />,
    isProtected: true,
    permission: "templates:view",
  },
  {
    path: "/templates/new",
    element: <TemplateFormPage />,
    isProtected: true,
    permission: "templates:create",
  },
  {
    path: "/templates/edit/:templateId",
    element: <TemplateFormPage />,
    isProtected: true,
    permission: "templates:edit",
  },
  {
    path: "/templates/preview/:id",
    element: <TemplatePreview />,
    isProtected: true,
    permission: "templates:preview",
  },

  // Document search + compare
  {
    path: "/document-search",
    element: <DocumentSearch />,
    isProtected: true,
    permission: "documents:search",
  },
  {
    path: "/document-compare",
    element: <DocumentCompare />,
    isProtected: true,
    permission: "documents:compare",
  },
  {
    path: "/document-compare/result/:compareId",
    element: <DocumentCompareResult />,
    isProtected: true,
    permission: "documents:compare",
  },

  // Workflows + notifications
  {
    path: "/workflow",
    element: <WorkflowListPage />,
    isProtected: true,
    permission: "workflows:view",
  },
  {
    path: "/notifications/manage",
    element: <NotificationManagement />,
    isProtected: true,
    permission: "notifications:view",
  },
  {
    path: "/files/:documentStoreId/web-scraper",
    element: <WebScraperJobList />,
    isProtected: true,
  },
  {
    path: "/files/:documentStoreId/connections",
    element: <FolderConnectionsPage />,
    isProtected: true,
  },
  {
    path: "/files/:documentStoreId/web-scraper/:jobId",
    element: <WebScraperJobDetail />,
    isProtected: true,
  },
  {
    path: "/files/:documentStoreId/spreadsheet-connector",
    element: <SpreadsheetConnectorList />,
    isProtected: true,
  },
];

const AppRoutes = () => {
  return (
    <>
      <SentryRoutes>
        {/* Token handler route for classical login (cookie-based) */}
        <Route path="/handle/token" element={<TokenCookieHandler />} />

        {/* Auth and public routes handled by AuthBaseLayout */}
        <Route element={<AuthBaseLayout />}>
          {/* Workflow create route */}
          <Route element={<PrivateRoute />}>
            <Route path="/workflow/create" element={<WorkflowLayout />}>
              <Route
                index
                element={
                  <RequirePermission permission="workflows:create">
                    <CreateWorkflowPage />
                  </RequirePermission>
                }
              />
            </Route>
          </Route>

          <Route element={<PrivateRoute />}>
            <Route path="/executions/:id" element={<WorkflowLayout />}>
              <Route
                index
                element={
                  <RequirePermission permission="workflows:view_executions">
                    <WorkFlowExecutions />
                  </RequirePermission>
                }
              />
            </Route>
          </Route>

          {/* Documentation - standalone without layout */}
          <Route element={<PrivateRoute />}>
            <Route path="/docs" element={<WorkflowNodeDocs />} />
            <Route path="/docs/:nodeType" element={<WorkflowNodeDocs />} />
          </Route>

          {/* Spreadsheet Connector OAuth callback - standalone without layout */}
          <Route
            path="/spreadsheet-connector"
            element={<SpreadsheetConnectorCallback />}
          />

          {/* OneDrive OAuth callback - standalone without layout */}
          <Route
            path="/workflow/connector/one-drive"
            element={<OneDriveAuthHandler />}
          />
          <Route
            path="/workflow/connector/gmail"
            element={<GmailAuthHandler />}
          />
          <Route
            path="/workflow/connector/outlook"
            element={<OutlookAuthHandler />}
          />

          {/* Protected routes wrapped in BaseLayout */}

          {/* Protected routes wrapped in BaseLayout */}
          <Route element={<PrivateRoute />}>
            <Route element={<BaseLayout />}>
              {routeConfig.map(
                ({ path, element, permission, anyOf }, index) => {
                  const gated =
                    permission || anyOf ? (
                      <RequirePermission permission={permission} anyOf={anyOf}>
                        {element}
                      </RequirePermission>
                    ) : (
                      element
                    );
                  return <Route key={index} path={path} element={gated} />;
                },
              )}
            </Route>
          </Route>

          {/* Standalone access-denied page (also rendered inline by RequirePermission) */}
          <Route path="/no-access" element={<NoAccess />} />

          {/* Standalone session-expired page */}
          <Route path="/session-expired" element={<SessionExpired />} />

          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route
          path="/register/success"
          element={<RegistrationSuccessMessage />}
        />
      </SentryRoutes>
    </>
  );
};

export default AppRoutes;
